/**
 * SuggestedPeople.jsx — "People you may know", with the reason attached.
 *
 * WHY THIS EXISTS
 *
 * A professional network whose feed is empty is not a slow product, it is a
 * broken one — and the honest reason a new Clincordant account sees nothing is that
 * it follows nobody. No amount of ranking fixes an empty graph. This is the
 * thing that fills it.
 *
 * DESIGN DECISIONS
 *
 * Every row says WHY.
 *   "Also in Cardiology" or "Followed by 3 people you follow". A list of
 *   strangers with no explanation is something people scroll past; a list with
 *   a reason is something they read. The server picks the single strongest
 *   reason rather than listing three, because three reads as a sales pitch.
 *
 * It disappears once it has done its job.
 *   Hidden entirely when there is nothing worth suggesting, and dismissable for
 *   the session. A permanent "people you may know" block on a feed that is
 *   already full is clutter, and the whole point is to stop needing it.
 *
 * Following someone removes them from the list, and does not reorder it.
 *   Re-ranking on every follow makes the remaining rows jump under the finger
 *   that just tapped — which on a phone means following the wrong person.
 *
 * Loaded once per mount, never polled.
 *   The suggestion set changes on the timescale of days. Re-fetching on every
 *   feed refresh would spend reads to show the same nine people.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { UserPlus, X, Users } from 'lucide-react';

import { functions } from '../firebase';
import { Avatar, Skeleton } from './ui';
import { VerifiedBadge } from './Verification';
import { useFollowActions } from '../hooks/useSocial';

const callSuggested = httpsCallable(functions, 'suggestedFollows');

function PersonRow({ person, onFollowed }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const { toggleFollow } = useFollowActions({ onError: () => setFailed(true) });

  const follow = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await toggleFollow(person.uid);
      onFollowed(person.uid);
    } catch {
      // useFollowActions already surfaced the reason; keep the row so the
      // person can try again rather than having it vanish as if it worked.
      setBusy(false);
    }
  }, [busy, toggleFollow, person.uid, onFollowed]);

  return (
    <li className="sugg__row">
      <Link to={`/u/${person.username}`} className="sugg__who">
        <Avatar
          profile={{
            uid: person.uid,
            displayName: person.displayName,
            avatarUrl: person.avatarUrl,
            avatarColour: person.avatarColour,
          }}
          size={40}
        />
        <span className="sugg__text">
          <span className="sugg__name">
            {person.displayName || `@${person.username}`}
            <VerifiedBadge tier={person.tier} />
          </span>
          {person.jobTitle && <span className="sugg__role">{person.jobTitle}</span>}
          <span className="sugg__reason">{failed ? 'Could not follow — try again' : person.reason}</span>
        </span>
      </Link>

      <button
        type="button"
        className="sugg__follow"
        onClick={follow}
        disabled={busy}
        aria-label={`Follow ${person.displayName || person.username}`}
      >
        <UserPlus size={15} aria-hidden="true" />
        <span>Follow</span>
      </button>
    </li>
  );
}

export default function SuggestedPeople() {
  const [people, setPeople] = useState(null);   // null = loading
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    callSuggested()
      .then((res) => { if (!cancelled) setPeople(res.data?.suggestions ?? []); })
      // Suggestions are a nicety. If the call fails — a cold function, an index
      // still building — the feed must carry on as though the block was never
      // there, rather than showing an error for something nobody asked for.
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, []);

  const remove = useCallback((uid) => {
    setPeople((prev) => (prev ?? []).filter((p) => p.uid !== uid));
  }, []);

  if (dismissed) return null;

  if (people === null) {
    return (
      <div className="card sugg" aria-busy="true">
        <span className="sr-only">Finding people to follow</span>
        <Skeleton height={18} width="55%" style={{ marginBottom: 14 }} />
        <Skeleton height={40} style={{ marginBottom: 10 }} />
        <Skeleton height={40} />
      </div>
    );
  }

  // Nothing worth suggesting is a reason to show nothing, not a reason to show
  // an empty state apologising for it.
  if (!people.length) return null;

  return (
    <section className="card sugg" aria-labelledby="sugg-heading">
      <header className="sugg__head">
        <Users size={16} aria-hidden="true" />
        <h2 id="sugg-heading">People you may know</h2>
        <button
          type="button"
          className="sugg__dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Hide suggestions"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>

      <ul className="sugg__list">
        {people.map((p) => (
          <PersonRow key={p.uid} person={p} onFollowed={remove} />
        ))}
      </ul>
    </section>
  );
}

export const SUGGESTED_CSS = `
.sugg { padding: 16px; margin-bottom: 14px; }

.sugg__head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.sugg__head > svg { color: var(--ink-faint); flex: 0 0 auto; }
.sugg__head h2 {
  margin: 0; flex: 1; min-width: 0;
  font-family: var(--font-display); font-size: var(--text-lg);
}
.sugg__dismiss {
  position: relative;
  display: grid; place-items: center;
  width: 30px; height: 30px; flex: 0 0 auto;
  border: 0; border-radius: 50%;
  background: transparent; color: var(--ink-faint); cursor: pointer;
}
.sugg__dismiss:hover { background: var(--bg-alt); color: var(--ink); }
/* Touch devices have no hover to reveal intent, so the hit area grows instead
   of the button — see the coarse-pointer block in lib/theme.js. */
.sugg__dismiss::before { content: ''; position: absolute; inset: -7px; }

.sugg__list { list-style: none; margin: 0; padding: 0; }

.sugg__row {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.sugg__row:last-child { border-bottom: 0; padding-bottom: 0; }

.sugg__who {
  display: flex; align-items: center; gap: 12px;
  flex: 1; min-width: 0;
  color: inherit; text-decoration: none;
}
.sugg__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.sugg__name {
  display: flex; align-items: center; gap: 5px; flex-wrap: wrap;
  font-size: var(--text-sm); font-weight: 650; color: var(--ink);
}
.sugg__who:hover .sugg__name { text-decoration: underline; }
.sugg__role {
  font-size: var(--text-xs); color: var(--ink-soft);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.sugg__reason { font-size: var(--text-xs); color: var(--ink-faint); }

.sugg__follow {
  display: inline-flex; align-items: center; gap: 6px;
  flex: 0 0 auto;
  min-height: 34px; padding: 6px 14px;
  border: 1px solid var(--primary); border-radius: var(--radius-full);
  background: transparent; color: var(--primary-text);
  font: inherit; font-size: var(--text-xs); font-weight: 600;
  cursor: pointer;
  transition: background var(--transition), color var(--transition);
}
.sugg__follow:hover:not(:disabled) { background: var(--primary); color: var(--on-accent); }
.sugg__follow:disabled { opacity: 0.5; cursor: default; }

/* Below 380 the Follow label costs more than it earns — the icon alone is
   unambiguous next to a person's name, and keeping the name readable matters
   more than the word. */
@media (max-width: 380px) {
  .sugg__follow span { display: none; }
  .sugg__follow { padding: 6px 10px; }
}
`;
